import React, { useState, useRef } from 'react';
import {
  Box, Button, Card, CardContent, Grid, Typography, Alert, CircularProgress,
  Stack, Fab, Paper, TextField, InputAdornment, Collapse, FormControl,
  InputLabel, Select, MenuItem, Pagination, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, useTheme, useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon, Refresh as RefreshIcon, Search as SearchIcon,
  FilterList as FilterListIcon, Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  TrendingUp as TrendingUpIcon, Delete as DeleteIcon,
} from '@mui/icons-material';
import InventoryIcon from '@mui/icons-material/Inventory';
import { Product, Category, Warehouse } from '../../../enums/product';
import { Brand, fetchActiveBrands } from '../brand/brandService';
import { CategoryService } from '../category/categoryService';
import { WarehouseService } from '../warehouse/warehouseService';
import ProductTable from './ProductTable';
import EnhancedProductDrawer from './ProductDrawer';
import BulkProductUpload from './BulkProductUpload';
import ToastNotification from '../common/Toastnotification';
import { useAuth } from '../../../contexts/AuthContext';
import { useProductsPaginated, useProductStats, useProductMutations } from '../../../hooks/useProducts';
import { ProductService } from './productService';

const PRODUCTS_PER_PAGE = 10;

interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

interface LoadingOverlayProps {
  wrapperRef: React.RefObject<HTMLDivElement | null>;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ wrapperRef }) => {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const theme = useTheme();
  const overlayBg = theme.palette.mode === 'dark'
    ? 'rgba(26, 29, 36, 0.85)'
    : 'rgba(255, 255, 255, 0.85)';

  React.useEffect(() => {
    const measure = () => {
      if (wrapperRef.current) setRect(wrapperRef.current.getBoundingClientRect());
    };
    measure();
    const el = wrapperRef.current;
    window.addEventListener('resize', measure);
    el?.addEventListener('scroll', measure);
    return () => {
      window.removeEventListener('resize', measure);
      el?.removeEventListener('scroll', measure);
    };
  }, [wrapperRef]);

  if (!rect) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: rect.top, left: rect.left,
        width: rect.width, height: rect.height,
        backgroundColor: overlayBg,
        backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100, pointerEvents: 'all', borderRadius: 'inherit',
      }}
    >
      <Box sx={{ textAlign: 'center' }}>
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading products...
        </Typography>
      </Box>
    </Box>
  );
};

const ProductsPage: React.FC = () => {
  const { currentUser, hasPermission, isSuperAdmin } = useAuth();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ── Reference data (not part of product cache) ────────────────────────────
  const [categories, setCategories] = useState<Category[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'add' | 'edit' | 'view'>('add');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<string>('');
  const [featuredLoading, setFeaturedLoading] = useState<string>('');
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Product | null>(null);
  const [deleteDialogLoading, setDeleteDialogLoading] = useState(false);
  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });

  // ── Pagination & filter state ─────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [stockFilter, setStockFilter] = useState<string>('all');
  const [variantFilter, setVariantFilter] = useState<string>('all');
  const [featuredFilter, setFeaturedFilter] = useState<string>('all');
  const [brandFilter, setBrandFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  const canView = isSuperAdmin || hasPermission('products.view');
  const canCreate = isSuperAdmin || hasPermission('products.create');
  const canEdit = isSuperAdmin || hasPermission('products.edit');
  const canDelete = isSuperAdmin || hasPermission('products.delete');
  // featured toggle is an inline edit action
  const canToggleFeatured = isSuperAdmin || hasPermission('products.edit');

  const tableWrapperRef = useRef<HTMLDivElement>(null);

  // ── React Query hooks ─────────────────────────────────────────────────────
  // `refetchProducts` is pulled out so handleProductSaved / handleDeleteConfirm
  // / handleBulkUploadComplete can force a fresh fetch after any mutation —
  // even when the user is already on page 1 (where resetToFirstPage() would be
  // a no-op and React Query would serve the stale cache instead of refetching).
  //
  // `refetchStats` keeps the summary cards (Total, Featured, etc.) in sync
  // after every create / edit / delete without needing a full page reload.
  const {
    data: productsData,
    isLoading: productsLoading,
    isFetching: productsIsFetching,
    isError: productsError,
    error: productsErrorObj,
    refetch: refetchProducts,         // ← NEW: force fresh fetch after mutations
  } = useProductsPaginated({
    page: currentPage,
    search: searchQuery,
    categoryFilter,
    stockFilter,
    variantFilter,
    featuredFilter,
    brandFilter,
    pageSize: PRODUCTS_PER_PAGE,
  });

  const {
    data: statsData,
    refetch: refetchStats,            // ← NEW: keep stat cards in sync
  } = useProductStats();

  const { remove, toggleFeatured } = useProductMutations();

  // ── Derived values ────────────────────────────────────────────────────────
  const products = productsData?.products ?? [];
  const totalProducts = productsData?.totalCount ?? 0;
  const totalPages = Math.ceil(totalProducts / PRODUCTS_PER_PAGE);
  const loading = productsLoading || productsIsFetching;

  const stats = statsData ?? {
    total: 0, inStock: 0, outOfStock: 0, lowStock: 0,
    withVariants: 0, withoutVariants: 0, featured: 0,
    nonFeatured: 0, categories: 0, brands: 0, totalStock: 0, averagePrice: 0,
  };

  // Load reference data (categories, warehouses, brands) — not cached via RQ
  React.useEffect(() => {
    CategoryService.getMainCategories()
      .then(data => setCategories(data))
      .catch(err => console.error('Error fetching categories:', err));
    WarehouseService.getActiveWarehouses()
      .then(data => setWarehouses(data))
      .catch(err => console.error('Error fetching warehouses:', err));
    fetchActiveBrands()
      .then(data => setBrands(data))
      .catch(err => console.error('Error fetching brands:', err));
  }, []);

  // ── Toast ─────────────────────────────────────────────────────────────────
  const showToast = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setToast({ open: true, message, severity });
  };
  const handleCloseToast = () => setToast(t => ({ ...t, open: false }));

  // ── Reset to page 1 ───────────────────────────────────────────────────────
  const resetToFirstPage = () => {
    setCurrentPage(1);
  };

  // ── Pagination handler ────────────────────────────────────────────────────
  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  // ── Search ────────────────────────────────────────────────────────────────
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchQuery(searchInput);
      resetToFirstPage();
    }
  };

  // ── Filter handlers ───────────────────────────────────────────────────────
  const handleCategoryFilterChange = (value: string) => { setCategoryFilter(value); resetToFirstPage(); };
  const handleStockFilterChange = (value: string) => { setStockFilter(value); resetToFirstPage(); };
  const handleVariantFilterChange = (value: string) => { setVariantFilter(value); resetToFirstPage(); };
  const handleFeaturedFilterChange = (value: string) => { setFeaturedFilter(value); resetToFirstPage(); };
  const handleBrandFilterChange = (value: string) => { setBrandFilter(value); resetToFirstPage(); };

  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setCategoryFilter('all');
    setStockFilter('all');
    setVariantFilter('all');
    setFeaturedFilter('all');
    setBrandFilter('all');
    resetToFirstPage();
  };

  const hasActiveFilters =
    searchQuery !== '' || categoryFilter !== 'all' || stockFilter !== 'all' ||
    variantFilter !== 'all' || featuredFilter !== 'all' || brandFilter !== 'all';

  const activeFiltersCount = [
    searchQuery !== '', categoryFilter !== 'all', stockFilter !== 'all',
    variantFilter !== 'all', featuredFilter !== 'all', brandFilter !== 'all',
  ].filter(Boolean).length;

  // ── Drawer handlers ───────────────────────────────────────────────────────
  const handleAddProduct = () => { setSelectedProduct(null); setDrawerMode('add'); setIsDrawerOpen(true); };
  const handleEditProduct = (product: Product) => { setSelectedProduct(product); setDrawerMode('edit'); setIsDrawerOpen(true); };
  const handleViewProduct = (product: Product) => { setSelectedProduct(product); setDrawerMode('view'); setIsDrawerOpen(true); };
  const handleDrawerClose = () => { setIsDrawerOpen(false); setSelectedProduct(null); };

  // ── handleProductSaved — the key refresh logic ────────────────────────────
  //
  // Called by EnhancedProductDrawer after a successful create or update.
  //
  // Why both resetToFirstPage() AND refetchProducts()?
  //
  //   resetToFirstPage() navigates back to page 1 so the user sees the newest
  //   product at the top. But if the user was already on page 1 when they
  //   edited an existing product, setCurrentPage(1) is a no-op — React Query
  //   sees the same query key and serves the stale cache, so the edited row
  //   would NOT update on screen.
  //
  //   refetchProducts() solves this: it bypasses the cache and fetches fresh
  //   data for the current query key unconditionally. Together the two calls
  //   cover both scenarios:
  //     • New product added on page 3  → page resets to 1 → query key changes
  //       → React Query fetches page 1 automatically.
  //     • Product edited while on page 1 → page stays 1 → query key unchanged
  //       → refetchProducts() forces the fetch that would otherwise be skipped.
  //
  //   refetchStats() keeps the summary cards (Total, Featured, etc.) in sync
  //   after every save without a full page reload.
  // ─────────────────────────────────────────────────────────────────────────
  const handleProductSaved = () => {
    handleDrawerClose();
    resetToFirstPage();    // navigate to page 1 so the new/edited product is visible
    refetchProducts();     // force fresh fetch even if page didn't change
    refetchStats();        // keep stat cards in sync
  };

  // ── handleBulkUploadComplete — same refresh logic after bulk import ────────
  //
  // BulkProductUpload can add many products at once. We apply the same
  // refetch pattern so the table and stat cards immediately reflect all the
  // newly imported rows without requiring a manual page reload.
  // ─────────────────────────────────────────────────────────────────────────
  const handleBulkUploadComplete = () => {
    resetToFirstPage();    // go back to page 1 to see freshly imported products
    refetchProducts();     // bypass cache — bulk upload may have added many rows
    refetchStats();        // totals / featured counts will have changed
  };

  const handleRefresh = () => {
    resetToFirstPage();
    refetchProducts();
    refetchStats();
    showToast('Products refreshed', 'info');
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteProduct = (product: Product) => setDeleteTarget(product);
  const handleDeleteCancel = () => setDeleteTarget(null);

const handleDeleteConfirm = async () => {
  if (!deleteTarget) return;
  try {
    setDeleteDialogLoading(true);
    setDeleteLoading(deleteTarget.id!);

    const hasOrders = await ProductService.productHasOrders(deleteTarget.id!);
    if (hasOrders) {
      showToast(
        `"${deleteTarget.name}" cannot be deleted because it is referenced by one or more orders.`,
        'error'
      );
      setDeleteTarget(null);
      return;
    }

    await remove.mutateAsync(deleteTarget.id!);
    setDeleteTarget(null);
    resetToFirstPage();
    refetchProducts();
    refetchStats();
    showToast('Product deleted successfully', 'success');
  } catch (err: any) {
    showToast('Failed to delete product: ' + (err.message || 'Unknown error'), 'error');
  } finally {
    setDeleteDialogLoading(false);
    setDeleteLoading('');
  }
};
  // ── Toggle featured ───────────────────────────────────────────────────────
  //
  // toggleFeatured is an inline action (no drawer involved), so React Query's
  // mutation invalidation should handle the cache automatically. However we
  // also call refetchStats() so the "Featured" stat card updates immediately
  // without waiting for the next background sync.
  // ─────────────────────────────────────────────────────────────────────────
  const handleToggleFeatured = async (product: Product) => {
    try {
      setFeaturedLoading(product.id || '');
      await toggleFeatured.mutateAsync({ productId: product.id!, isFeatured: !product.isFeatured });
      refetchStats();       // ← keep Featured count card in sync
      showToast(
        product.isFeatured ? 'Product removed from featured' : 'Product marked as featured',
        'success'
      );
    } catch (error: any) {
      showToast('Failed to update featured status: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setFeaturedLoading('');
    }
  };

  // ── Initial full-page loading state ───────────────────────────────────────
  if (loading && products.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px" flexDirection="column">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ mt: 2 }}>Loading products...</Typography>
      </Box>
    );
  }

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <InventoryIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2, opacity: 0.7 }} />
            <Typography variant="h5" gutterBottom>Access Denied</Typography>
            <Typography variant="body1" color="text.secondary">
              You don't have permission to view products. Please contact your administrator.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        height: { md: '100%' },
        display: 'flex',
        flexDirection: 'column',
        overflow: { xs: 'visible', md: 'hidden' },
        p: { xs: 2, sm: 3 },
      }}
    >
      {/* ── Fixed Header ── */}
      <Box sx={{ flexShrink: 0 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <InventoryIcon sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
                Products
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage your product inventory
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ display: { xs: 'none', md: 'flex' } }}>
            {canCreate && (
              <Button onClick={handleAddProduct} variant="contained" startIcon={<AddIcon />}>
                Add Product
              </Button>
            )}
          </Stack>
        </Stack>

        {/* {productsError && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => {}}
            action={<Button color="inherit" size="small" onClick={handleRefresh} startIcon={<RefreshIcon />}>Retry</Button>}>
            {(productsErrorObj as Error)?.message || 'Failed to fetch products'}
          </Alert>
        )} */}

        {/* ── Stats Cards ── */}
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: 3 }} alignItems="stretch">
          {[
            {
              label: 'Total Products', value: stats.total,
              sub: (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 }} />
                    <Typography variant="caption" color="success.main" noWrap>{stats.inStock} In Stock</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main', flexShrink: 0 }} />
                    <Typography variant="caption" color="error.main" noWrap>{stats.outOfStock} Out of Stock</Typography>
                  </Box>
                </>
              ),
            },
            {
              label: 'Simple Products', value: stats.withoutVariants,
              sub: <Typography variant="caption" color="text.secondary" noWrap>without variants</Typography>,
            },
            {
              label: 'With Variants', value: stats.withVariants,
              sub: <Typography variant="caption" color="text.secondary" noWrap>configurable products</Typography>,
            },
            {
              label: 'Featured', value: stats.featured,
              sub: <Typography variant="caption" color="text.secondary" noWrap>of {stats.total} total</Typography>,
            },
          ].map((card) => (
            <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }} key={card.label}>
              <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography color="text.secondary" gutterBottom variant="body2" noWrap>{card.label}</Typography>
                  <Typography variant="h5" component="div" color="text.primary" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
                    {card.value}
                  </Typography>
                  <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>{card.sub}</Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* ── Search & Filters ── */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <TextField
              placeholder="Search by name, SKU, description... (Press Enter)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              sx={{ flexGrow: 1, maxWidth: { sm: 400 } }}
              size="small"
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
            <Stack direction="row" alignItems="center" spacing={1}>
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant={hasActiveFilters ? 'contained' : 'outlined'}
                startIcon={<FilterListIcon />}
                endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                size="small"
              >
                Filters {hasActiveFilters && `(${activeFiltersCount})`}
              </Button>
              {hasActiveFilters && (
                <Button onClick={clearFilters} variant="text" size="small" startIcon={<ClearIcon />}>
                  Clear all
                </Button>
              )}
            </Stack>
          </Stack>

          <Collapse in={showFilters}>
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={categoryFilter}
                      onChange={(e) => handleCategoryFilterChange(e.target.value)}
                      label="Category"
                    >
                      <MenuItem value="all">All Categories</MenuItem>
                      {categories.map((category) => (
                        <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Brand</InputLabel>
                    <Select
                      value={brandFilter}
                      onChange={(e) => handleBrandFilterChange(e.target.value)}
                      label="Brand"
                    >
                      <MenuItem value="all">All Brands</MenuItem>
                      <MenuItem value="none">No Brand</MenuItem>
                      {brands.map((brand) => (
                        <MenuItem key={brand.id} value={brand.id}>{brand.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Stock Status</InputLabel>
                    <Select
                      value={stockFilter}
                      onChange={(e) => handleStockFilterChange(e.target.value)}
                      label="Stock Status"
                    >
                      <MenuItem value="all">All Products</MenuItem>
                      <MenuItem value="inStock">In Stock Only</MenuItem>
                      <MenuItem value="outOfStock">Out of Stock Only</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Product Type</InputLabel>
                    <Select
                      value={variantFilter}
                      onChange={(e) => handleVariantFilterChange(e.target.value)}
                      label="Product Type"
                    >
                      <MenuItem value="all">All Types</MenuItem>
                      <MenuItem value="withVariants">With Variants</MenuItem>
                      <MenuItem value="withoutVariants">Without Variants</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Featured</InputLabel>
                    <Select
                      value={featuredFilter}
                      onChange={(e) => handleFeaturedFilterChange(e.target.value)}
                      label="Featured"
                    >
                      <MenuItem value="all">All Products</MenuItem>
                      <MenuItem value="featured">Featured Only</MenuItem>
                      <MenuItem value="nonFeatured">Non-Featured Only</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                    <Typography variant="body2" color="text.secondary">
                      {hasActiveFilters
                        ? `Found ${products.length} products matching your filters`
                        : `Showing ${products.length} products on this page`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Paper>
      </Box>

      {/* ── Scrollable Body ── */}
      <Box sx={{ flex: 1, overflow: { xs: 'visible', md: 'auto' }, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>

        {products.length === 0 && !loading && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <Card sx={{ width: '100%', maxWidth: 500 }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <TrendingUpIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  {hasActiveFilters ? 'No Products Match Your Filters' : 'No Products Found'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                  {hasActiveFilters
                    ? 'Try adjusting your search or filter criteria.'
                    : 'No products available yet. Create your first product to get started.'}
                </Typography>
                {hasActiveFilters
                  ? <Button onClick={clearFilters} variant="outlined" startIcon={<ClearIcon />}>Clear Filters</Button>
                  : canCreate && <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddProduct}>Add Product</Button>
                }
              </CardContent>
            </Card>
          </Box>
        )}

        {products.length > 0 && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>
            <Box
              ref={tableWrapperRef}
              sx={{
                flex: 1,
                overflow: { xs: 'auto', md: 'auto' },
                maxHeight: { md: '100%' },
                mb: 2,
                position: 'relative',
                minHeight: { xs: 'auto', md: 0 },
              }}
            >
              <ProductTable
                products={products}
                categories={categories}
                warehouses={warehouses}
                brands={brands}
                onView={handleViewProduct}
                onEdit={handleEditProduct}
                onDelete={handleDeleteProduct}
                onToggleFeatured={handleToggleFeatured}
                deleteLoading={deleteLoading}
                featuredLoading={featuredLoading}
                isDark={isDark}
                canEdit={canEdit}           // ADD
                canDelete={canDelete}       // ADD
                canToggleFeatured={canToggleFeatured} // ADD
              />
            </Box>

            {loading && <LoadingOverlay wrapperRef={tableWrapperRef} />}

            <Paper elevation={2} sx={{ flexShrink: 0, borderRadius: 1 }}>
              <Box sx={{ py: 2, px: 2 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 1.5, sm: 3 }}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: '100%' }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Showing {totalProducts === 0 ? 0 : (currentPage - 1) * PRODUCTS_PER_PAGE + 1}–
                    {Math.min(currentPage * PRODUCTS_PER_PAGE, totalProducts)} of {totalProducts} products
                  </Typography>
                  <Pagination
                    count={Math.max(1, totalPages)}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                    showFirstButton
                    showLastButton
                    disabled={loading || totalPages <= 1}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Page {currentPage} of {Math.max(1, totalPages)}
                  </Typography>
                </Stack>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      {/* ── Drawer ── */}
      <EnhancedProductDrawer
        isOpen={isDrawerOpen}
        mode={drawerMode}
        product={selectedProduct}
        onClose={handleDrawerClose}
        onSave={handleProductSaved}
        onShowToast={showToast}
      />

      {/* ── Bulk Upload ── */}
      <BulkProductUpload
        open={isBulkUploadOpen}
        onClose={() => setIsBulkUploadOpen(false)}
        onComplete={handleBulkUploadComplete}
        onShowToast={showToast}
      />

      {/* ── Delete Dialog ── */}
      <Dialog open={!!deleteTarget} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Product</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>"{deleteTarget?.name}"</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={deleteDialogLoading}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteDialogLoading}
            startIcon={deleteDialogLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={handleCloseToast}
      />

      {/* ── Mobile FAB ── */}
      {canCreate && (
        <Fab
          color="primary"
          aria-label="add product"
          sx={{ position: 'fixed', bottom: 24, right: 24, display: { xs: 'flex', md: 'none' } }}
          onClick={handleAddProduct}
        >
          <AddIcon />
        </Fab>
      )}
    </Box>
  );
};

export default ProductsPage;